import { ReportElementData } from "@/shared/types/element";
import { useTheme } from "@mui/material/styles";
import { PaxElementWrapper, PaxElementWrapperProps, PaxTable, PaxTableProps } from "@ncino/pax-ui";
import { useState } from "react";
import { resolveElementData } from "./util";
import {
  Report_ElementQueryOptionsInput,
  Report_ElementStatus,
  Report_ElementType,
} from "@/services/graphql/reactQuery";

/* Example usage of the PaxListElement:
  <PaxListElement
    sx={{ maxWidth: "90vw" }}
    paragraph="Lorem ipsum dolor sit amet consectetur adipisicing elit. Adipisci cumque voluptates nesciunt ipsa iste dolorem beatae eveniet laboriosam officiis sapiente, officia quia facilis voluptatem. Deleniti impedit pariatur itaque quaerat aspernatur."
    note="this is a test note"
    elementData={listData}
    title={"Test List"}
    striped
    selectable
    onMoreOptions={() => window.alert("clicked on more options")}
    onEdit={() => window.alert("clicked on on edit")}
    selectedRows={selectedRows}
    onSelect={(rowIdx) =>
      setSelectedRows((prev) => prev.map((v, i) => (i === rowIdx ? !v : v)))
    }
    onSelectAll={() =>
      setSelectedRows((prev) => {
        const allSelected = prev.every(Boolean);
        return prev.map(() => !allSelected);
      })
    }
  />
*/
interface PaxListElementBaseProps {
  selectable?: boolean;
  selectedRows?: boolean[]; // indexes should be 1:1 with rows
  onSelect?: (rowIdx: number) => void;
  onSelectAll?: () => void;
  // Override the page-scoped header-checkbox derivation for whole-set (across pages) selection.
  selectAllChecked?: boolean;
  selectAllIndeterminate?: boolean;
  showIndex?: boolean; // toggles the numbered index column, defaults to true
  tableSx?: PaxTableProps["sx"]; // targets inner PaxTable/TableContainer
  tableHeaderVariant?: "dark" | "light";
  // External sort control (for server-side sorting)
  sortColumnIndex?: number | null;
  sortDirection?: "asc" | "desc";
  onSortChange?: (colIndex: number) => void;
}

interface PaxTableElementBaseProps {
  elementData: ReportElementData;
  settings?: Report_ElementQueryOptionsInput;
}

type PaxTableElementProps = PaxTableElementBaseProps &
  Omit<PaxElementWrapperProps, "children"> &
  Omit<PaxTableProps, "children"> & {
    disableSort?: boolean;
  };

export type PaxListElementProps = PaxListElementBaseProps & PaxTableElementProps;

export default function PaxListElement({
  // element wrapper props
  title = "Table",
  showHeader,
  paragraph,
  note,
  onMoreOptions,
  onEdit,
  sx, // by convention, sx applies to the outer-most element
  elevated,
  customHeader,
  // pax list props
  selectable,
  selectedRows,
  onSelect,
  onSelectAll,
  selectAllChecked,
  selectAllIndeterminate,
  showIndex,
  tableSx,
  tableHeaderVariant,
  sortColumnIndex: externalSortColumnIndex,
  sortDirection: externalSortDirection,
  onSortChange: externalOnSortChange,
  disableSort,
  // the rest are pax table props
  ...paxTableProps
}: PaxListElementProps) {
  const theme = useTheme();
  const [internalSortColumnIndex, setInternalSortColumnIndex] = useState<number | null>(null);
  const [internalSortDirection, setInternalSortDirection] = useState<"asc" | "desc">("asc");

  // Use external sort control if provided, otherwise use internal state
  const sortColumnIndex =
    externalSortColumnIndex !== undefined ? externalSortColumnIndex : internalSortColumnIndex;
  const sortDirection = externalSortDirection || internalSortDirection;

  const elementWrapperProps = {
    title,
    showHeader,
    paragraph,
    note,
    onMoreOptions,
    onEdit,
    elevated,
    sx,
    customHeader,
  };

  const listProps = {
    selectable,
    selectedRows,
    onSelect,
    onSelectAll,
    selectAllChecked,
    selectAllIndeterminate,
    showIndex,
    tableHeaderVariant,
  };

  paxTableProps.stripeColor = paxTableProps.stripeColor
    ? paxTableProps.stripeColor
    : theme.palette.info.selected;

  function handleSortChange(colIndex: number) {
    // Use external handler if provided
    if (externalOnSortChange) {
      externalOnSortChange(colIndex);
      return;
    }

    // Otherwise use internal state
    if (internalSortColumnIndex === colIndex) {
      setInternalSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setInternalSortColumnIndex(colIndex);
      setInternalSortDirection("asc");
    }
  }

  const sortConfig = { sortColumnIndex, sortDirection, onSortChange: handleSortChange };
  const isProcessing =
    paxTableProps.elementData.status === Report_ElementStatus.Processing ||
    paxTableProps.elementData.status === Report_ElementStatus.Queued;

  return (
    <PaxElementWrapper {...elementWrapperProps} title={title}>
      {/* table */}
      <PaxTable
        {...paxTableProps}
        stripeColor={paxTableProps.stripeColor}
        sx={{
          ...(typeof tableSx === "object" && !Array.isArray(tableSx) ? tableSx : {}),
          borderBottom: "none",
        }}
      >
        {resolveElementData(
          Report_ElementType.List,
          paxTableProps.elementData,
          { ...paxTableProps, ...listProps },
          theme,
          disableSort ? undefined : sortConfig,
          isProcessing
        )}
      </PaxTable>
    </PaxElementWrapper>
  );
}
